import type {
  ActiveManualOverride,
  PendingManualOverrideAction,
} from '@/services/inventory'
import { areFieldValuesEquivalent } from '@/utils/fieldOverrideState'

export type DraftLike = {
  fields?: Record<string, unknown> | null
  patch?: Record<string, unknown> | null
  operationalPatch?: Record<string, unknown> | null
  manualOverrideActions?: Record<string, unknown> | null
  intent?: string | null
  deleteRequestedAt?: string | null
}

function hasKeys(value: Record<string, unknown> | null | undefined): boolean {
  return Boolean(value && typeof value === 'object' && Object.keys(value).length > 0)
}

export function isVehicleDraftMeaningful(draft: DraftLike | null | undefined): boolean {
  if (!draft) return false
  if (draft.intent === 'DELETE' || draft.intent === 'CREATE' || draft.intent === 'RESTORE') return true
  if (draft.deleteRequestedAt) return true
  return (
    hasKeys(draft.fields) ||
    hasKeys(draft.patch) ||
    hasKeys(draft.operationalPatch) ||
    hasKeys(draft.manualOverrideActions)
  )
}

export function areDraftFieldValuesEquivalent(
  field: string,
  left: unknown,
  right: unknown,
): boolean {
  return areFieldValuesEquivalent(field, left, right)
}

export function prunePatchNoOps(params: {
  patch: Record<string, unknown>
  persistedValues: Record<string, unknown>
}): { patch: Record<string, unknown>; prunedFields: string[] } {
  const patch: Record<string, unknown> = {}
  const prunedFields: string[] = []

  for (const [field, value] of Object.entries(params.patch)) {
    if (areDraftFieldValuesEquivalent(field, value, params.persistedValues[field])) {
      prunedFields.push(field)
    } else {
      patch[field] = value
    }
  }

  return { patch, prunedFields }
}

export function isReleasePreviewKnown(activeOverride: ActiveManualOverride | undefined): boolean {
  if (!activeOverride) return false
  if (activeOverride.releasePreviewStatus === 'unavailable') return false
  if (activeOverride.releasePreviewStatus === 'resolved' || activeOverride.releasePreviewStatus === 'empty') {
    return true
  }
  return (
    Object.prototype.hasOwnProperty.call(activeOverride, 'valueIfReleasedNow') &&
    activeOverride.valueIfReleasedNow !== undefined
  ) || (
    Object.prototype.hasOwnProperty.call(activeOverride, 'currentFeedValue') &&
    activeOverride.currentFeedValue !== undefined
  )
}

export function resolveReleaseTarget(activeOverride: ActiveManualOverride | undefined): unknown {
  if (!activeOverride || !isReleasePreviewKnown(activeOverride)) return undefined
  if (Object.prototype.hasOwnProperty.call(activeOverride, 'valueIfReleasedNow')) {
    return activeOverride.valueIfReleasedNow
  }
  return activeOverride.currentFeedValue
}

export type FeedBackedFieldSaveIntent = 'release' | 'noop' | 'override'

export function resolveFeedBackedFieldSaveIntent(params: {
  field: string
  submittedValue: unknown
  currentBaseValue: unknown
  activeOverride?: ActiveManualOverride
  pendingAction?: PendingManualOverrideAction
  hasPolicyChange?: boolean
}): FeedBackedFieldSaveIntent {
  const active = params.activeOverride

  if (active) {
    if (
      isReleasePreviewKnown(active) &&
      areDraftFieldValuesEquivalent(
        params.field,
        params.submittedValue,
        resolveReleaseTarget(active),
      )
    ) {
      return 'release'
    }

    if (
      !params.hasPolicyChange &&
      areDraftFieldValuesEquivalent(params.field, params.submittedValue, active.manualValue)
    ) {
      return 'noop'
    }

    return 'override'
  }

  if (
    !params.hasPolicyChange &&
    areDraftFieldValuesEquivalent(params.field, params.submittedValue, params.currentBaseValue)
  ) {
    return 'noop'
  }

  return params.pendingAction || params.hasPolicyChange ? 'override' : 'override'
}
